import { Component, ElementRef, HostListener, ViewChild, inject } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { DatePipe, NgClass } from '@angular/common';
import { ScheduleDatesFilterModel } from 'src/app/models/filter-date.model';
import { SupplierPaymentModel } from 'src/app/models/supplier-payment.model';
import { SupplierPaymentService } from 'src/app/services/supplier-payment.service';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import jsPDF from 'jspdf';
import { ToastService } from 'src/app/services/toast.service';
import autoTable from 'jspdf-autotable';
import { MatIcon } from '@angular/material/icon';
import { MatButton, MatIconButton } from '@angular/material/button';
import { PageHeaderComponent } from 'src/app/shared/ui/page-header/page-header.component';
import { EmptyStateComponent } from 'src/app/shared/ui/empty-state/empty-state.component';
import { DatesFilterComponent } from '../../dates-filter/dates-filter.component';
import { MatMenu, MatMenuItem } from '@angular/material/menu';
import { IconMenuButtonComponent } from 'src/app/shared/components/icon-menu-button/icon-menu-button.component';
import { MatFormField } from '@angular/material/input';
import { MatSelect, MatOption } from '@angular/material/select';
import { FilterBySupplierPaymentPipe } from '../../../shared/pipes/filter-by-supplier-payment.pipe';
import { FilterByPaymentPagesPipe } from '../../../shared/pipes/filter-by-payment-pages.pipe';

@Component({
    selector: 'app-supplier-payment',
    templateUrl: './supplier-payment.component.html',
    styleUrls: ['./supplier-payment.component.scss'],
    imports: [FormsModule, ReactiveFormsModule, MatIcon, DatesFilterComponent, NgClass, MatMenu, MatMenuItem, MatFormField, MatSelect, MatOption, DatePipe, FilterBySupplierPaymentPipe, FilterByPaymentPagesPipe, MatButton, MatIconButton, PageHeaderComponent, EmptyStateComponent, IconMenuButtonComponent]
})
export class SupplierPaymentComponent {
  private readonly paymentSvc = inject(SupplierPaymentService);
  datepipe = inject(DatePipe);
  toastr = inject(ToastService);

  @ViewChild('contentToPrint', { static: false }) contentToPrint!: ElementRef;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  displayedColumns: string[] = [
    'supplier',
    'day',
    'job',
    'product',
    'city',
    'client',
    'payment',
    'extra',
  ];

  observations: string = '';
  observationsAvailability: { rowIndex: number; paymentIndex: number } = {
    rowIndex: -1,
    paymentIndex: -1,
  };
  
  searchTextControl = new FormControl('');
  filteredEntriesArray: [string, SupplierPaymentModel[]][] = [];

  filteringDate!: ScheduleDatesFilterModel;
  isVisible: boolean = false;
  payments: SupplierPaymentModel[] = [];
  entriesArray: [string, SupplierPaymentModel[]][] = [];
  groupedData: Map<string, SupplierPaymentModel[]> = new Map<
    string,
    SupplierPaymentModel[]
  >();

  pages: number = 0;
  regXPage: number[] = [5, 10, 15, 20];
  seleccionado: number = 5;
  disablePrint: boolean = false;

  isProjection: boolean = false;
  /** A request is on its way: "no payments" is only true once the answer arrived. */
  loading = false;
  loadError = false;

  ngOnInit() {
    this.getPayments();

    this.searchTextControl.valueChanges.subscribe((value: string | null) => {
      const searchText = value || '';
      this.pages = 0;

      this.filteredEntriesArray = this.entriesArray.filter((entry) =>
        entry[1].some((payment) =>
          this.removeAccents(payment.taskType)
            ?.toLowerCase()
            .includes(this.removeAccents(searchText).toLowerCase()) ||
          payment.taskDate.toString().includes(searchText) ||
          this.removeAccents(payment.productName)
            ?.toLowerCase()
            .includes(this.removeAccents(searchText).toLowerCase()) ||
          this.removeAccents(payment.clientName)
            ?.toLowerCase()
            .includes(this.removeAccents(searchText).toLowerCase()) ||
          this.removeAccents(payment.clientCity)
            ?.toLowerCase()
            .includes(this.removeAccents(searchText).toLowerCase()) ||
          this.removeAccents(payment.contractorName)
            ?.toLowerCase()
            .includes(this.removeAccents(searchText).toLowerCase()) ||
          payment.amount?.toString().includes(searchText)
        )
      );
    });


  }

  clearSearch() {
    this.searchTextControl.setValue('');
  }

  removeAccents(str: string) {
    if (!str) return ''; // Si str es null, devuelve una cadena vacía

    const accents: { [key: string]: string } = {
      á: 'a',
      é: 'e',
      í: 'i',
      ó: 'o',
      ú: 'u',
      Á: 'A',
      É: 'E',
      Í: 'I',
      Ó: 'O',
      Ú: 'U',
    };
    return str.replaceAll(/[áéíóúÁÉÍÓÚ]/g, (match) => accents[match] || match);
  }

  getPayments() {
    this.startLoading();
    this.paymentSvc.getAllPayments(this.isProjection).subscribe({ error: () => this.failLoading(), next: (data) => {
      this.loading = false;
      this.payments = data;
      // Agrupando registros en un Map por contractorName
      this.groupedData = this.groupByContractorName(this.payments);
      // variable para evitar error en tiempo de ejecución en la consola
      this.entriesArray = Array.from(this.groupedData.entries());
      // variable para aplicar filtro de escritura en tiempo real
      this.filteredEntriesArray = this.entriesArray;
    } });
  }

  private startLoading() {
    this.loading = true;
    this.loadError = false;
  }

  private failLoading() {
    this.loading = false;
    this.loadError = true;
    this.payments = [];
    this.entriesArray = [];
    this.filteredEntriesArray = [];
  }

  groupByContractorName(data: SupplierPaymentModel[]) {
    const groupedDataTemp = new Map<string, SupplierPaymentModel[]>();
    data.forEach((item) => {
      const key = item.contractorName;
      if (groupedDataTemp.has(key)) {
        groupedDataTemp.get(key)!.push(item);
      } else {
        groupedDataTemp.set(key, [item]);
      }
    });

    // Ordenar los arrays internos
    groupedDataTemp.forEach((value) => {
      value.sort((a, b) => {
        const dateA = new Date(a.taskDate);
        const dateB = new Date(b.taskDate);
        return dateA.getTime() - dateB.getTime();
      });
    });

    return groupedDataTemp;
  }

  isLastContractorPayment(
    groupedData: Map<string, SupplierPaymentModel[]>,
    entry: [string, SupplierPaymentModel[]],
    index: number
  ): boolean {
    if (index < groupedData.size - 1) {
      const nextEntry = Array.from(groupedData.entries())[index + 1];
      return entry[0] !== nextEntry[0];
    }
    return true;
  }

  getTotalsByContractorName(payment: SupplierPaymentModel[]) {
    let totals: number = 0;

    payment.forEach((register) => {
      totals += register.amount;
    });

    return totals;
  }

  onSubmit(dates?: ScheduleDatesFilterModel, visible?: boolean) {
    if (dates != undefined) {
      this.startLoading();
      this.paymentSvc
        .getAllPayments(this.isProjection, dates.from, dates.to)
        .subscribe({ error: () => this.failLoading(), next: (data) => {
          this.loading = false;
          this.payments = data;
          this.groupedData = this.groupByContractorName(data);
          this.entriesArray = Array.from(this.groupedData.entries());
          this.filteredEntriesArray = this.entriesArray;
          if (visible != undefined) {
            this.toggleVisibility(visible);
          } else {
            this.toggleVisibility();
          }
        } });
    } else if (visible != undefined) {
      this.toggleVisibility(visible);
      this.getPayments();
    } else {
      this.toggleVisibility();
      this.getPayments();
    }
  }

  updateObservation(paymentId: string, observation: string) {
    this.paymentSvc.updateObservation(paymentId, observation).subscribe({
      next: () => {
        this.toastr.success(
          'Observacion actualizada',
          'Las observaciones del pago se actualizaron correctamente.'
        );
        this.inputDisable();
        this.onSubmit(this.filteringDate, false);
      },
      error: (err) => {
        this.toastr.error(
          'Error',
          'No se pudo actualizar las observaciones:' + err
        );
      },
    });
  }

  toggleVisibility(value?: boolean) {
    if (value != undefined) {
      this.isVisible = value;
    } else {
      this.isVisible = !this.isVisible;
    }
  }

  inputEnable(index: number, payIndex: number, payment: SupplierPaymentModel) {
    this.observationsAvailability = { rowIndex: index, paymentIndex: payIndex };
    this.observations = payment.observation;
  }

  inputDisable() {
    this.observationsAvailability = { rowIndex: -1, paymentIndex: -1 };
  }

  setDates(dates: ScheduleDatesFilterModel) {
    if (dates.from == '' && dates.to == '') {
      this.getPayments();
      this.toggleVisibility();
    } else {
      this.filteringDate = dates;
      this.onSubmit(this.filteringDate);
    }
    this.disablePrint = false;
    this.pages = 0;
  }

  @HostListener('document:keydown', ['$event'])
  captureCtrlP(event: KeyboardEvent) {
    if (event.ctrlKey && event.key === 'p') {
      event.preventDefault();
      this.exportAsPDF();
    }
  }

  /** "1 - 5 de 12": the rows on this page out of the total, the same label as the other lists' paginators. */
  get rangeLabel(): string {
    const total = this.filteredEntriesArray.length;
    if (total === 0) {
      return '0 de 0';
    }
    return `${this.pages + 1} - ${Math.min(this.pages + this.seleccionado, total)} de ${total}`;
  }

  nextPage() {
    if (this.filteredEntriesArray.length > this.pages + this.seleccionado)
      this.pages += this.seleccionado;
  }

  previousPage() {
    if (this.pages > 0) this.pages -= this.seleccionado;
  }

  disableClick() {
    this.disablePrint = true;
    setTimeout(() => {
      this.disablePrint = false;
    }, 10000);
  }

  wrapCellText(text: string, maxWords: number) {
    if (text) {
      const words = text.split(' ');
      let result = '';
      for (let i = 0; i < words.length; i++) {
        result += words[i] + ' ';
        if ((i + 1) % maxWords === 0) {
          result += '\n';
        }
      }
      return result.trim();
    }
    return [];
  }

  exportAsPDF() {
    this.toastr.warning(
      'Generando impresión',
      'Generando el archivo de impresión, por favor no actualice el navegador.'
    );
    this.disableClick();
    const doc = new jsPDF('landscape');

    //Configuración de columnas de la tabla
    const columns = [
      { header: 'Día', dataKey: 'day' },
      { header: 'Tipo de tarea', dataKey: 'taskType' },
      { header: 'Producto', dataKey: 'product' },
      { header: 'Ciudad', dataKey: 'city' },
      { header: 'Cliente', dataKey: 'client' },
      { header: 'Adicional', dataKey: 'additional' },
      { header: 'Precio', dataKey: 'amount' },
      { header: 'Importe', dataKey: 'amountWithAdditional' },
      { header: 'Extra', dataKey: 'observation' },
    ];

    const rows: any[] = [];
    this.filteredEntriesArray.forEach((entry) => {
      const contractorName = entry[0];
      const payments = entry[1];

      //Agregar una fila para el nombre del contratista
      rows.push({
        isContractorHeader: true,
        day: this.wrapCellText(contractorName, 2),
        taskType: '',
        product: '',
        city: '',
        client: '',
        additional: '',
        amount: '',
        amountWithAdditional: '',
        observation: '',
      });

      // Agregar las filas de pagos
      payments.forEach((payment) => {
        rows.push({
          isContractorHeader: false,
          day: this.datepipe.transform(payment.taskDate, 'dd/MM/yyyy'),
          taskType: this.wrapCellText(payment.taskType, 3),
          product: this.wrapCellText(payment.productName, 2),
          city: this.wrapCellText(payment.clientCity, 2),
          client: this.wrapCellText(payment.clientName, 2),
          additional: `${payment.additional}%`,
          amount: payment.amount,
          amountWithAdditional: payment.amountWithAdditional,
          observation: this.wrapCellText(payment.observation, 4),
        });
      });

      // Agregar una fila para los totales
      rows.push({
        isContractorHeader: false,
        day: '',
        taskType: '',
        product: '',
        city: '',
        client: '',
        additional: 'Total:',
        amount: this.getTotalsByContractorName(payments),
        amountWithAdditional: '',
        observation: '',
      });
    });

    //Generar la tabla en pdf
    autoTable(doc, {
      startY: 3,
      columns: columns,
      body: rows,
      didParseCell: (data) => {
        const { row, cell } = data;

        // Aplica formato solo a las filas del contratista
        if (
          row.raw &&
          'isContractorHeader' in row.raw &&
          row.raw.isContractorHeader
        ) {
          cell.styles.fillColor = [212, 226, 237];
          cell.styles.fontSize = 10;
          cell.styles.fontStyle = 'bold';
        }
      },
      styles: {
        cellWidth: 'wrap',
        fontSize: 10,
        cellPadding: 2,
        overflow: 'linebreak',
      },
      headStyles: { fillColor: '#296ba1' },
      margin: { top: 3, bottom: 8, left: 3, right: 3 },
      didDrawPage: (data) => {
        doc.setFontSize(10);
        doc.text(
          data.pageNumber.toString(),
          doc.internal.pageSize.width - 10,
          doc.internal.pageSize.height - 5
        );
      },
      pageBreak: 'auto', // Habilitar salto de página automático
    });

    // Genearate the pdf as a Blob and open it in a new tab
    const pdfBlob = doc.output('blob'); // Obtain the PDF as a Blob
    const url = URL.createObjectURL(pdfBlob); // Create URL for the Blob
    window.open(url, '_blank'); // Open the Blob in a new tab
  }

  closeModal() {
    this.isVisible = false;
  }

  setRegsXPage(event: any) {
    this.seleccionado = event.target.value;
    this.pages = 0;
  }

  changeProjection() {
    if (
      !this.filteringDate ||
      (this.filteringDate.from == '' && this.filteringDate.to == '')
    ) {
      this.getPayments();
    } else {
      this.onSubmit(this.filteringDate, false);
    }
  }
}
